import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { AddRounded, DeleteOutline } from '@mui/icons-material';
import DataTable from '../components/DataTable';
import { fetchCategoriasPadre } from '../api/categorias';
import {
  createGananciaManual,
  createPublicidad,
  createRetiro,
  deleteGananciaManual,
  deletePublicidad,
  deleteRetiro,
  fetchGananciasManuales,
  fetchPublicidad,
  fetchRetiros,
  fetchRentabilidad,
  updateGananciaManual,
  updatePublicidad,
  updateRetiro,
} from '../api/finanzas';
import { useConfirm } from '../contexts/ConfirmContext';
import { useLoading } from '../contexts/LoadingContext';
import { useToast } from '../contexts/ToastContext';

const today = () => new Date().toISOString().slice(0, 10);
const currency = (value) => new Intl.NumberFormat('es-SV', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
}).format(Number(value || 0));
const apiMessage = (error, fallback) => error?.response?.data?.error || fallback;

function MetricCard({ title, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800 }}>{value}</Typography>
    </Paper>
  );
}

function CalculationBreakdown({ summary = {} }) {
  const manual = Number(summary.ganancia_manual || 0);
  const system = Number(summary.ganancia_bruta || 0);
  const commission = Number(summary.comision_transportista || 0);
  const advertising = Number(summary.publicidad || 0);
  const beforeExpenses = manual + system;
  const deductions = commission + advertising;
  const operatingProfit = Number(summary.ganancia_operativa ?? (beforeExpenses - deductions));
  const donations = Number(summary.retiros_donacion || 0);
  const salaries = Number(summary.retiros_salario || 0);
  const otherWithdrawals = Number(summary.otros_retiros || 0);
  const withdrawals = Number(summary.retiros_total || 0);
  const available = Number(summary.ganancia_disponible || 0);
  const purchases = Number(summary.compras || 0);
  const balance = available - purchases;

  const terms = [
    { label: 'Ganancias manuales', value: manual, sign: '' },
    { label: 'Ganancia bruta sistema', value: system, sign: '+' },
    { label: 'Comisión', value: commission, sign: '−' },
    { label: 'Publicidad', value: advertising, sign: '−' },
    { label: 'Ganancia operativa', value: operatingProfit, sign: '=' },
  ];
  const withdrawalTerms = [
    { label: 'Ganancia operativa', value: operatingProfit, sign: '' },
    { label: 'Donaciones', value: donations, sign: '−' },
    { label: 'Salarios desde ganancia', value: salaries, sign: '−' },
    { label: 'Otros retiros', value: otherWithdrawals, sign: '−' },
    { label: 'Ganancia disponible final', value: available, sign: '=' },
  ];

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>Detalle del cálculo</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 2 }}>
        La ganancia disponible se obtiene sumando las ganancias y restando todos los gastos aplicables.
      </Typography>

      <Stack
        direction="row"
        spacing={1.25}
        useFlexGap
        flexWrap="wrap"
        alignItems="stretch"
      >
        {terms.map((term) => (
          <React.Fragment key={term.label}>
            {term.sign && (
              <Typography variant="h5" sx={{ alignSelf: 'center', fontWeight: 800, px: 0.25 }}>
                {term.sign}
              </Typography>
            )}
            <Box
              sx={{
                minWidth: 155,
                flex: term.sign === '=' ? '1 1 190px' : '1 1 155px',
                p: 1.5,
                borderRadius: 2,
                bgcolor: term.sign === '=' ? 'success.light' : 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary">{term.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{currency(term.value)}</Typography>
            </Box>
          </React.Fragment>
        ))}
      </Stack>

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1, fontWeight: 800 }}>
        Aplicación de la ganancia
      </Typography>
      <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" alignItems="stretch">
        {withdrawalTerms.map((term) => (
          <React.Fragment key={term.label}>
            {term.sign && (
              <Typography variant="h5" sx={{ alignSelf: 'center', fontWeight: 800, px: 0.25 }}>
                {term.sign}
              </Typography>
            )}
            <Box
              sx={{
                minWidth: 155,
                flex: term.sign === '=' ? '1 1 190px' : '1 1 155px',
                p: 1.5,
                borderRadius: 2,
                bgcolor: term.sign === '=' ? 'success.light' : 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary">{term.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{currency(term.value)}</Typography>
            </Box>
          </React.Fragment>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body1">
        {currency(manual)} + {currency(system)} = <strong>{currency(beforeExpenses)}</strong> antes de gastos.
        {' '}Las deducciones suman <strong>{currency(deductions)}</strong> ({currency(commission)} de comisión
        {' '}+ {currency(advertising)} de publicidad), dejando una ganancia operativa de <strong>{currency(operatingProfit)}</strong>.
        {' '}Después de restar <strong>{currency(withdrawals)}</strong> en donaciones, salarios y otros retiros,
        quedan <strong>{currency(available)}</strong> disponibles.
      </Typography>
      <Alert severity={balance >= 0 ? 'success' : 'warning'} sx={{ mt: 2 }}>
        {balance >= 0
          ? `Después de cubrir ${currency(purchases)} en compras, queda un excedente de ${currency(balance)}.`
          : `De ${currency(purchases)} en compras, todavía faltan ${currency(Math.abs(balance))} por cubrir.`}
      </Alert>
    </Paper>
  );
}

const emptyManual = () => ({
  id: null,
  nombre: '',
  fecha: today(),
  observaciones: '',
  detalles: [{ categoria_id: '', monto: '' }],
});

const emptyAdvertising = () => ({
  id: null,
  nombre: 'Publicidad',
  categoria_id: '',
  fecha_desde: today(),
  fecha_hasta: today(),
  monto: '',
  observaciones: '',
});

const emptyWithdrawal = () => ({
  id: null,
  tipo: 'donacion',
  fecha: today(),
  categoria_id: '',
  monto: '',
  beneficiario: '',
  observaciones: '',
});

const withdrawalLabels = {
  donacion: 'Donación',
  salario: 'Salario desde ganancia',
  retiro_personal: 'Retiro personal',
  otro: 'Otro retiro',
};

export default function Finanzas() {
  const [tab, setTab] = useState('ganancias');
  const [categories, setCategories] = useState([]);
  const [manualRows, setManualRows] = useState([]);
  const [manualPage, setManualPage] = useState(1);
  const [manualTotal, setManualTotal] = useState(0);
  const [advertisingRows, setAdvertisingRows] = useState([]);
  const [advertisingPage, setAdvertisingPage] = useState(1);
  const [advertisingTotal, setAdvertisingTotal] = useState(0);
  const [withdrawalRows, setWithdrawalRows] = useState([]);
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [withdrawalTotal, setWithdrawalTotal] = useState(0);
  const [profitability, setProfitability] = useState({ rows: [], summary: {} });
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportCategory, setReportCategory] = useState('');
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [advertisingOpen, setAdvertisingOpen] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManual);
  const [advertisingForm, setAdvertisingForm] = useState(emptyAdvertising);
  const [withdrawalForm, setWithdrawalForm] = useState(emptyWithdrawal);
  const pageSize = 20;
  const initialRef = useRef(true);
  const { stop } = useLoading();
  const { showToast } = useToast();
  const confirm = useConfirm();

  const loadManual = useCallback(async (page) => {
    const data = await fetchGananciasManuales({ page, pageSize });
    setManualRows(data.rows || []);
    setManualTotal(Number(data.total || 0));
  }, []);

  const loadAdvertising = useCallback(async (page) => {
    const data = await fetchPublicidad({ page, pageSize });
    setAdvertisingRows(data.rows || []);
    setAdvertisingTotal(Number(data.total || 0));
  }, []);

  const loadWithdrawals = useCallback(async (page) => {
    const data = await fetchRetiros({ page, pageSize });
    setWithdrawalRows(data.rows || []);
    setWithdrawalTotal(Number(data.total || 0));
  }, []);

  const loadProfitability = useCallback(async ({ from = '', to = '', categoriaId = '' } = {}) => {
    setProfitabilityLoading(true);
    try {
      const data = await fetchRentabilidad({
        from: from || undefined,
        to: to || undefined,
        categoriaId: categoriaId || undefined,
      });
      setProfitability(data || { rows: [], summary: {} });
    } finally {
      setProfitabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([fetchCategoriasPadre(), loadManual(1), loadAdvertising(1), loadWithdrawals(1), loadProfitability()])
      .then(([categoryResponse]) => {
        if (active) setCategories(categoryResponse.data || []);
      })
      .catch((error) => showToast({ message: apiMessage(error, 'No se pudieron cargar los datos financieros'), severity: 'error' }))
      .finally(() => {
        if (active) setLoading(false);
        if (initialRef.current) { stop(); initialRef.current = false; }
      });
    return () => { active = false; };
  }, [loadAdvertising, loadManual, loadProfitability, loadWithdrawals, showToast, stop]);

  const changeManualPage = async (page) => {
    setManualPage(page);
    setLoading(true);
    try { await loadManual(page); } finally { setLoading(false); }
  };

  const changeAdvertisingPage = async (page) => {
    setAdvertisingPage(page);
    setLoading(true);
    try { await loadAdvertising(page); } finally { setLoading(false); }
  };

  const changeWithdrawalPage = async (page) => {
    setWithdrawalPage(page);
    setLoading(true);
    try { await loadWithdrawals(page); } finally { setLoading(false); }
  };

  const openNewManual = () => { setManualForm(emptyManual()); setManualOpen(true); };
  const openEditManual = (row) => {
    setManualForm({
      id: row.id,
      nombre: row.nombre || '',
      fecha: String(row.fecha || '').slice(0, 10),
      observaciones: row.observaciones || '',
      detalles: (row.detalles || []).map((item) => ({
        categoria_id: String(item.categoria_id), monto: String(item.monto),
      })),
    });
    setManualOpen(true);
  };

  const updateManualDetail = (index, field, value) => setManualForm((current) => ({
    ...current,
    detalles: current.detalles.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )),
  }));

  const addManualDetail = () => setManualForm((current) => ({
    ...current, detalles: [...current.detalles, { categoria_id: '', monto: '' }],
  }));

  const removeManualDetail = (index) => setManualForm((current) => ({
    ...current, detalles: current.detalles.filter((_, itemIndex) => itemIndex !== index),
  }));

  const manualAmount = useMemo(() => manualForm.detalles.reduce(
    (sum, item) => sum + (Number(item.monto) || 0), 0
  ), [manualForm.detalles]);

  const saveManual = async () => {
    setSaving(true);
    try {
      const payload = {
        ...manualForm,
        detalles: manualForm.detalles.map((item) => ({
          categoria_id: Number(item.categoria_id), monto: Number(item.monto),
        })),
      };
      if (manualForm.id) await updateGananciaManual(manualForm.id, payload);
      else await createGananciaManual(payload);
      setManualOpen(false);
      setManualPage(1);
      await loadManual(1);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Ganancia manual guardada', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo guardar la ganancia'), severity: 'error' });
    } finally { setSaving(false); }
  };

  const removeManual = async (row) => {
    if (!await confirm({
      title: 'Eliminar ganancia manual',
      content: `¿Eliminar "${row.nombre}" por ${currency(row.monto_total)}?`,
      confirmText: 'Eliminar', confirmColor: 'error',
    })) return;
    try {
      await deleteGananciaManual(row.id);
      await loadManual(manualPage);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Ganancia manual eliminada', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo eliminar la ganancia'), severity: 'error' });
    }
  };

  const openNewAdvertising = () => { setAdvertisingForm(emptyAdvertising()); setAdvertisingOpen(true); };
  const openEditAdvertising = (row) => {
    setAdvertisingForm({
      id: row.id,
      nombre: row.nombre || '',
      categoria_id: String(row.categoria_id || ''),
      fecha_desde: String(row.fecha_desde || '').slice(0, 10),
      fecha_hasta: String(row.fecha_hasta || '').slice(0, 10),
      monto: String(row.monto || ''),
      observaciones: row.observaciones || '',
    });
    setAdvertisingOpen(true);
  };

  const saveAdvertising = async () => {
    setSaving(true);
    try {
      const payload = {
        ...advertisingForm,
        categoria_id: Number(advertisingForm.categoria_id),
        monto: Number(advertisingForm.monto),
      };
      if (advertisingForm.id) await updatePublicidad(advertisingForm.id, payload);
      else await createPublicidad(payload);
      setAdvertisingOpen(false);
      setAdvertisingPage(1);
      await loadAdvertising(1);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Gasto de publicidad guardado', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo guardar la publicidad'), severity: 'error' });
    } finally { setSaving(false); }
  };

  const removeAdvertising = async (row) => {
    if (!await confirm({
      title: 'Eliminar gasto de publicidad',
      content: `¿Eliminar "${row.nombre}" por ${currency(row.monto)}?`,
      confirmText: 'Eliminar', confirmColor: 'error',
    })) return;
    try {
      await deletePublicidad(row.id);
      await loadAdvertising(advertisingPage);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Gasto de publicidad eliminado', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo eliminar la publicidad'), severity: 'error' });
    }
  };

  const openNewWithdrawal = () => { setWithdrawalForm(emptyWithdrawal()); setWithdrawalOpen(true); };
  const openEditWithdrawal = (row) => {
    setWithdrawalForm({
      id: row.id,
      tipo: row.tipo || 'donacion',
      fecha: String(row.fecha || '').slice(0, 10),
      categoria_id: String(row.categoria_id || ''),
      monto: String(row.monto || ''),
      beneficiario: row.beneficiario || '',
      observaciones: row.observaciones || '',
    });
    setWithdrawalOpen(true);
  };

  const saveWithdrawal = async () => {
    setSaving(true);
    try {
      const payload = {
        ...withdrawalForm,
        categoria_id: Number(withdrawalForm.categoria_id),
        monto: Number(withdrawalForm.monto),
      };
      if (withdrawalForm.id) await updateRetiro(withdrawalForm.id, payload);
      else await createRetiro(payload);
      setWithdrawalOpen(false);
      setWithdrawalPage(1);
      await loadWithdrawals(1);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Retiro de ganancia guardado', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo guardar el retiro'), severity: 'error' });
    } finally { setSaving(false); }
  };

  const removeWithdrawal = async (row) => {
    if (!await confirm({
      title: 'Eliminar retiro de ganancia',
      content: `¿Eliminar ${withdrawalLabels[row.tipo] || 'el retiro'} por ${currency(row.monto)}?`,
      confirmText: 'Eliminar', confirmColor: 'error',
    })) return;
    try {
      await deleteRetiro(row.id);
      await loadWithdrawals(withdrawalPage);
      await loadProfitability({ from: reportFrom, to: reportTo, categoriaId: reportCategory });
      showToast({ message: 'Retiro eliminado', severity: 'success' });
    } catch (error) {
      showToast({ message: apiMessage(error, 'No se pudo eliminar el retiro'), severity: 'error' });
    }
  };

  const manualColumns = [
    { Header: 'Inventario', accessor: 'nombre' },
    { Header: 'Fecha de cierre', accessor: (row) => String(row.fecha || '').slice(0, 10) },
    { Header: 'Detalle por categoría', accessor: (row) => (
      (row.detalles || []).map((item) => `${item.categoria}: ${currency(item.monto)}`).join(' · ')
    ) },
    { Header: 'Ganancia total', accessor: (row) => currency(row.monto_total) },
  ];

  const advertisingColumns = [
    { Header: 'Publicidad', accessor: 'nombre' },
    { Header: 'Categoría', accessor: 'categoria' },
    { Header: 'Desde', accessor: (row) => String(row.fecha_desde || '').slice(0, 10) },
    { Header: 'Hasta', accessor: (row) => String(row.fecha_hasta || '').slice(0, 10) },
    { Header: 'Monto', accessor: (row) => currency(row.monto) },
  ];

  const withdrawalColumns = [
    { Header: 'Tipo', accessor: (row) => withdrawalLabels[row.tipo] || row.tipo },
    { Header: 'Fecha', accessor: (row) => String(row.fecha || '').slice(0, 10) },
    { Header: 'Categoría', accessor: 'categoria' },
    { Header: 'Beneficiario', accessor: (row) => row.beneficiario || '—' },
    { Header: 'Monto', accessor: (row) => currency(row.monto) },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, pb: 5 }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Finanzas</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Registra ganancias históricas y publicidad sin modificar el inventario físico.
        </Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab value="ganancias" label="Ganancias manuales" />
          <Tab value="publicidad" label="Publicidad" />
          <Tab value="retiros" label="Retiros de ganancia" />
          <Tab value="rentabilidad" label="Rentabilidad" />
        </Tabs>
      </Paper>

      {tab === 'ganancias' && (
        <Stack spacing={2}>
          <Alert severity="info">
            Usa un registro para cada cierre histórico (Inventario 1, Inventario 2, etc.) y reparte su ganancia entre categorías.
          </Alert>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Ganancias históricas</Typography>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openNewManual}>Nueva ganancia</Button>
          </Stack>
          <DataTable
            rows={manualRows}
            columns={manualColumns}
            loading={loading}
            onEdit={openEditManual}
            onDelete={removeManual}
            pagination={{ page: manualPage, pageSize, total: manualTotal, onPageChange: changeManualPage }}
          />
        </Stack>
      )}

      {tab === 'publicidad' && (
        <Stack spacing={2}>
          <Alert severity="info">
            El monto corresponde al período completo. Los reportes lo distribuyen proporcionalmente por día al consultar un rango parcial. Los períodos de una misma categoría no pueden cruzarse.
          </Alert>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Gastos de publicidad</Typography>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openNewAdvertising}>Nueva publicidad</Button>
          </Stack>
          <DataTable
            rows={advertisingRows}
            columns={advertisingColumns}
            loading={loading}
            onEdit={openEditAdvertising}
            onDelete={removeAdvertising}
            pagination={{ page: advertisingPage, pageSize, total: advertisingTotal, onPageChange: changeAdvertisingPage }}
          />
        </Stack>
      )}

      {tab === 'retiros' && (
        <Stack spacing={2}>
          <Alert severity="info">
            Las donaciones y salarios tomados de la ganancia reducen el dinero disponible, pero se muestran separados de la utilidad operativa.
          </Alert>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Retiros de ganancia</Typography>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openNewWithdrawal}>Nuevo retiro</Button>
          </Stack>
          <DataTable
            rows={withdrawalRows}
            columns={withdrawalColumns}
            loading={loading}
            onEdit={openEditWithdrawal}
            onDelete={removeWithdrawal}
            pagination={{ page: withdrawalPage, pageSize, total: withdrawalTotal, onPageChange: changeWithdrawalPage }}
          />
        </Stack>
      )}

      {tab === 'rentabilidad' && (
        <Stack spacing={2}>
          <Alert severity="info">
            Este cálculo suma las ganancias manuales y las ventas finalizadas; luego resta comisiones, publicidad, donaciones, salarios y otros retiros registrados.
          </Alert>

          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                label="Desde" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={reportFrom} onChange={(event) => setReportFrom(event.target.value)}
              />
              <TextField
                label="Hasta" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={reportTo} onChange={(event) => setReportTo(event.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  label="Categoría" value={reportCategory}
                  onChange={(event) => setReportCategory(event.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={String(category.id)}>{category.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={profitabilityLoading}
                onClick={() => loadProfitability({
                  from: reportFrom, to: reportTo, categoriaId: reportCategory,
                }).catch((error) => showToast({
                  message: apiMessage(error, 'No se pudo calcular la rentabilidad'), severity: 'error',
                }))}
              >
                Calcular
              </Button>
              <Button
                disabled={profitabilityLoading || (!reportFrom && !reportTo && !reportCategory)}
                onClick={() => {
                  setReportFrom(''); setReportTo(''); setReportCategory('');
                  loadProfitability().catch((error) => showToast({
                    message: apiMessage(error, 'No se pudo calcular la rentabilidad'), severity: 'error',
                  }));
                }}
              >
                Ver todo
              </Button>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Ganancias manuales" value={currency(profitability.summary?.ganancia_manual)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Ganancia bruta del sistema" value={currency(profitability.summary?.ganancia_bruta)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Publicidad aplicada" value={currency(profitability.summary?.publicidad)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Donaciones y retiros" value={currency(profitability.summary?.retiros_total)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Ganancia disponible final" value={currency(profitability.summary?.ganancia_disponible)} />
            </Grid>
          </Grid>

          <CalculationBreakdown summary={profitability.summary} />

          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Ganancia aplicada a compras</Typography>
                <Typography variant="body2" color="text.secondary">
                  Compras: {currency(profitability.summary?.compras)} · Pendiente: {currency(profitability.summary?.pendiente_por_cubrir)}
                </Typography>
              </Box>
              <Chip
                color={Number(profitability.summary?.porcentaje_cubierto || 0) >= 100 ? 'success' : 'primary'}
                label={`${Number(profitability.summary?.porcentaje_cubierto || 0).toFixed(2)}% cubierto`}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, Number(profitability.summary?.porcentaje_cubierto || 0)))}
              color={Number(profitability.summary?.porcentaje_cubierto || 0) >= 100 ? 'success' : 'primary'}
              sx={{ height: 10, borderRadius: 5, mb: 2 }}
            />
            <DataTable
              rows={profitability.rows || []}
              loading={profitabilityLoading}
              columns={[
                { Header: 'Categoría', accessor: 'categoria' },
                { Header: 'Manual', accessor: (row) => currency(row.ganancia_manual) },
                { Header: 'Sistema', accessor: (row) => currency(row.ganancia_bruta) },
                { Header: 'Comisión', accessor: (row) => currency(row.comision_transportista) },
                { Header: 'Publicidad', accessor: (row) => currency(row.publicidad) },
                { Header: 'Donaciones', accessor: (row) => currency(row.retiros_donacion) },
                { Header: 'Salarios', accessor: (row) => currency(row.retiros_salario) },
                { Header: 'Otros retiros', accessor: (row) => currency(row.otros_retiros) },
                { Header: 'Disponible final', accessor: (row) => currency(row.ganancia_disponible) },
                { Header: 'Compras', accessor: (row) => currency(row.compras) },
                { Header: 'Cubierto', accessor: (row) => row.porcentaje_cubierto == null ? '—' : `${Number(row.porcentaje_cubierto).toFixed(2)}%` },
                { Header: 'Pendiente', accessor: (row) => currency(row.pendiente_por_cubrir) },
                { Header: 'Inventario restante', accessor: (row) => currency(row.inventario_restante) },
              ]}
              showActions={false}
            />
          </Paper>
        </Stack>
      )}

      <Dialog open={withdrawalOpen} onClose={() => !saving && setWithdrawalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{withdrawalForm.id ? 'Editar retiro de ganancia' : 'Nuevo retiro de ganancia'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo" value={withdrawalForm.tipo}
                onChange={(event) => setWithdrawalForm((current) => ({ ...current, tipo: event.target.value }))}
              >
                {Object.entries(withdrawalLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fecha" type="date" InputLabelProps={{ shrink: true }} value={withdrawalForm.fecha}
              onChange={(event) => setWithdrawalForm((current) => ({ ...current, fecha: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría" value={withdrawalForm.categoria_id}
                onChange={(event) => setWithdrawalForm((current) => ({ ...current, categoria_id: event.target.value }))}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>{category.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Monto" type="number" inputProps={{ min: 0.01, step: 0.01 }} value={withdrawalForm.monto}
              onChange={(event) => setWithdrawalForm((current) => ({ ...current, monto: event.target.value }))}
            />
            <TextField
              label="Beneficiario (opcional)" value={withdrawalForm.beneficiario}
              onChange={(event) => setWithdrawalForm((current) => ({ ...current, beneficiario: event.target.value }))}
            />
            <TextField
              label="Observaciones" multiline minRows={2} value={withdrawalForm.observaciones}
              onChange={(event) => setWithdrawalForm((current) => ({ ...current, observaciones: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={saveWithdrawal} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualOpen} onClose={() => !saving && setManualOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{manualForm.id ? 'Editar ganancia manual' : 'Nueva ganancia manual'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nombre del inventario" fullWidth value={manualForm.nombre}
                onChange={(event) => setManualForm((current) => ({ ...current, nombre: event.target.value }))}
                placeholder="Inventario 1"
              />
              <TextField
                label="Fecha de cierre" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={manualForm.fecha}
                onChange={(event) => setManualForm((current) => ({ ...current, fecha: event.target.value }))}
              />
            </Stack>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Detalle por categoría</Typography>
            {manualForm.detalles.map((detail, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="center">
                <FormControl fullWidth>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    label="Categoría" value={detail.categoria_id}
                    onChange={(event) => updateManualDetail(index, 'categoria_id', event.target.value)}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={String(category.id)}>{category.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Ganancia" type="number" value={detail.monto}
                  onChange={(event) => updateManualDetail(index, 'monto', event.target.value)}
                  inputProps={{ min: 0.01, step: 0.01 }} sx={{ minWidth: 170 }}
                />
                <Tooltip title="Quitar categoría">
                  <span>
                    <IconButton
                      color="error" disabled={manualForm.detalles.length === 1}
                      onClick={() => removeManualDetail(index)}
                    ><DeleteOutline /></IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
            <Button startIcon={<AddRounded />} onClick={addManualDetail} sx={{ alignSelf: 'flex-start' }}>
              Agregar categoría
            </Button>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Ganancia total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{currency(manualAmount)}</Typography>
            </Paper>
            <TextField
              label="Observaciones" multiline minRows={2} value={manualForm.observaciones}
              onChange={(event) => setManualForm((current) => ({ ...current, observaciones: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={saveManual} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={advertisingOpen} onClose={() => !saving && setAdvertisingOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{advertisingForm.id ? 'Editar publicidad' : 'Nueva publicidad'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nombre o campaña" value={advertisingForm.nombre}
              onChange={(event) => setAdvertisingForm((current) => ({ ...current, nombre: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría" value={advertisingForm.categoria_id}
                onChange={(event) => setAdvertisingForm((current) => ({ ...current, categoria_id: event.target.value }))}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>{category.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Desde" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={advertisingForm.fecha_desde}
                onChange={(event) => setAdvertisingForm((current) => ({ ...current, fecha_desde: event.target.value }))}
              />
              <TextField
                label="Hasta" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={advertisingForm.fecha_hasta}
                onChange={(event) => setAdvertisingForm((current) => ({ ...current, fecha_hasta: event.target.value }))}
              />
            </Stack>
            <TextField
              label="Monto del período" type="number" value={advertisingForm.monto}
              onChange={(event) => setAdvertisingForm((current) => ({ ...current, monto: event.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Observaciones" multiline minRows={2} value={advertisingForm.observaciones}
              onChange={(event) => setAdvertisingForm((current) => ({ ...current, observaciones: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvertisingOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={saveAdvertising} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
